/* Picks which admin dashboard to mount.
 *
 *   org admin, All Branches (overview mode) -> OrgOverviewDashboard
 *   a named branch (branch mode) or a branch admin -> AdminDashboard
 *
 * It also owns the reaction to the header branch dropdown. Only EmployeeList
 * listened for BRANCH_FILTER_CHANGED_EVENT before, so picking a branch in
 * Dashboard Overview left the dashboard showing the previous scope until the
 * next full reload. Keying AdminDashboard on the filter remounts it, so it
 * re-fetches with the new ?branch= — and swapping to/from the org view happens
 * on the same signal.
 */

import React, { useEffect, useState } from "react";

import AdminDashboard from "./AdminDashboard";
import OrgOverviewDashboard from "./OrgOverviewDashboard";
import {
    BRANCH_FILTER_CHANGED_EVENT,
    getOverviewBranchFilter,
    isOrgOverview,
    shouldShowOrgAdminBranchUI,
} from "../../utils/branchSession";
import { SESSION_STORAGE_HYDRATED_EVENT } from "../../hooks/use-navigation";

const DashboardSwitch = () => {
    const [branchFilter, setBranchFilter] = useState(() => getOverviewBranchFilter());

    useEffect(() => {
        const onBranchFilterChange = () => setBranchFilter(getOverviewBranchFilter());
        const onPolicyChange = () => setBranchFilter(getOverviewBranchFilter());
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchFilterChange);
        window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, onPolicyChange);
        return () => {
            window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchFilterChange);
            window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, onPolicyChange);
        };
    }, []);

    // No branch filter while in overview = "All Branches" = the org view. A
    // branch admin is never in overview mode, so they always get AdminDashboard.
    // When org_admin_branch_selection is off, overview mode is cleared and this
    // always mounts the full operational dashboard instead.
    if (shouldShowOrgAdminBranchUI() && isOrgOverview() && !branchFilter) {
        return <OrgOverviewDashboard />;
    }

    return <AdminDashboard key={branchFilter || "all"} />;
};

export default DashboardSwitch;
